import { useEffect } from "react";
import { Box, Flex, BoxProps } from "@chakra-ui/react";
import { IExtendedSearchItem } from "src/api/client/extended-search.api";
import { WfLinkGhost } from "src/components/base/wf-link";
import { GoToWikifolioButton } from "src/components/common/buttons/go-to-wikifolio-button/go-to-wikifolio-button";
import { WatchlistButton } from "src/components/common/buttons/watchlist-button/watchlist-button";
import { TraderAvatar } from "src/components/common/trader-avatar";
import { WikifolioOgChartImage } from "src/components/common/wikifolio-og-chart-image";
import { CardRankings } from "src/components/pages/search/components/card-result/components/card-rankings";
import { useSearchStore } from "src/components/pages/search/store/search-store";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useWikifolioStore } from "src/stores/wikifolio-store";
import { getTrackingUrlParam } from "src/utils/search-tracking-util";

const SEARCH_RESULT_CARD_TEST_ID = "search-result-card";
const SEARCH_RESULT_CARD_WIKIFOLIO_LINK_TEST_ID = "search-result-card-wikifolio-link";
const SEARCH_RESULT_CARD_TRADER_PROFILE_LINK_TEST_ID = "search-result-card-trader-profile-link";

export interface ISearchResultCardProps extends IExtendedSearchItem, BoxProps {
    currency?: string;
}

export const SearchResultCard = ({
    wikifolioId,
    symbol,
    wikifolioShortDescription,
    trader,
    rankings,
    currency,
    isOnWatchlist = false,
    isInvestable = false,
    ...boxProps
}: ISearchResultCardProps) => {
    const { buildTraderProfileLink, buildWikifolioLink } = useLinkBuilder();
    const { watchlist, setOnWatchlist } = useWikifolioStore();
    const { query } = useSearchStore();
    const getUserName = useGetUserName();

    const placement = "extended";

    useEffect(() => {
        if (wikifolioId && isOnWatchlist) {
            setOnWatchlist(wikifolioId, isOnWatchlist);
        }
    }, [wikifolioId, isOnWatchlist, setOnWatchlist]);

    const traderProfileLink = buildTraderProfileLink(trader.nickName);
    const wikifolioLink = buildWikifolioLink(symbol, getTrackingUrlParam({ placement, query }));

    return (
        <Box
            height="max-content"
            flex={1}
            boxShadow="base"
            rounded="8px"
            flexDir="column"
            minWidth={0}
            p={[2, 3]}
            data-test-id={SEARCH_RESULT_CARD_TEST_ID}
            {...boxProps}
        >
            <Flex mb={2}>
                <TraderAvatar flexShrink={0} size="md" trader={trader} mr={1} href={traderProfileLink} />
                <Flex flexDir="column" flexGrow={1} overflow="hidden">
                    <WfLinkGhost href={wikifolioLink} noOfLines={1} wordBreak="break-all" data-test-id={SEARCH_RESULT_CARD_WIKIFOLIO_LINK_TEST_ID}>
                        {wikifolioShortDescription}
                    </WfLinkGhost>
                    <WfLinkGhost
                        href={traderProfileLink}
                        fontWeight="normal"
                        fontSize="sm"
                        noOfLines={1}
                        wordBreak="break-all"
                        data-test-id={SEARCH_RESULT_CARD_TRADER_PROFILE_LINK_TEST_ID}
                    >
                        {getUserName(trader)}
                    </WfLinkGhost>
                </Flex>
            </Flex>
            <WfLinkGhost href={wikifolioLink}>
                {/* TODO replace after https://wikifolio.atlassian.net/browse/FA-20 is done */}
                <WikifolioOgChartImage
                    loading={"lazy"}
                    ratio={21 / 9}
                    wikifolioId={wikifolioId}
                    shortDescription={wikifolioShortDescription}
                    mb={2}
                    mx={[-2, -3]}
                />
            </WfLinkGhost>
            <Box mb={2}>
                <CardRankings rankings={rankings} currency={currency} />
            </Box>
            <Flex>
                <GoToWikifolioButton flex={1} variant="outline" href={wikifolioLink} size="sm" />
                <WatchlistButton
                    size="sm"
                    sx={{
                        span: { m: "0" },
                    }}
                    ml={2}
                    p={0}
                    omitTitle
                    disabled={!isInvestable}
                    wikifolioShortDescription={wikifolioShortDescription}
                    wikifolioId={wikifolioId}
                    isOnWatchlist={watchlist[wikifolioId]}
                    setOnWatchlist={setOnWatchlist}
                />
            </Flex>
        </Box>
    );
};
